import DOMPurify from 'dompurify';

const RICH_TEXT_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li',
  'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'code', 'pre',
];
const RICH_TEXT_ATTRS = ['href', 'title', 'target', 'rel'];
const INVISIBLE_RE = /[\u200B-\u200D\u2060\uFEFF]/g;
// Security normalization intentionally targets ASCII control characters.
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;
const NAME_RE = /^[\p{L}\p{M}\p{N} .'-]+$/u;
const PHONE_RE = /^\+?[0-9 ()-]+$/;
const NUMBER_RE = /^-?(?:\d+|\d*\.\d+)$/;

const ATTACK_PATTERNS = [
  { re: /<\s*\/?\s*(?:script|iframe|object|embed|svg|img|form|input|style|link|meta)\b/i, message: 'HTML/script content is not allowed' },
  { re: /\b(?:javascript|vbscript)\s*:|\bdata\s*:\s*text\/html/i, message: 'Unsafe URL protocol is not allowed' },
  { re: /\bon[a-z]+\s*=/i, message: 'HTML event handlers are not allowed' },
  { re: /\bunion\s+(?:all\s+)?select\b|\b(?:drop\s+table|delete\s+from|insert\s+into|xp_cmdshell)\b/i, message: 'Database command patterns are not allowed' },
  { re: /(?:['"]\s*)?\b(?:or|and)\s+\d+\s*=\s*\d+/i, message: 'Injection patterns are not allowed' },
  { re: /["']?\$(?:where|gt|gte|lt|lte|ne|regex|or|and|expr|function)\b/i, message: 'MongoDB operators are not allowed in input values' },
  { re: /(?:\.\.[/\\])|(?:%2e|%2f|%5c)/i, message: 'Path traversal patterns are not allowed' },
  { re: /&&|\|\||\$\(|\$\{|`|\b(?:rm\s+-rf|cat\s+\/etc\/|whoami\b|curl\s+https?:\/\/|wget\s+https?:\/\/)/i, message: 'Command execution patterns are not allowed' },
];

function decodeHtmlEntities(value) {
  if (typeof document === 'undefined') return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

export function canonicalizeForSecurityScan(value) {
  let result = String(value ?? '');
  for (let i = 0; i < 2; i += 1) {
    try {
      const decoded = decodeURIComponent(result);
      if (decoded === result) break;
      result = decoded;
    } catch { break; }
  }
  result = decodeHtmlEntities(result)
    .replace(/\\x([0-9a-f]{2})/gi, (_, hex) => String.fromCharCode(Number.parseInt(hex, 16)))
    .replace(/\\u([0-9a-f]{4})/gi, (_, hex) => String.fromCharCode(Number.parseInt(hex, 16)));
  return result.normalize('NFC');
}

export function sanitizeRichText(value) {
  return DOMPurify.sanitize(String(value ?? ''), {
    ALLOWED_TAGS: RICH_TEXT_TAGS,
    ALLOWED_ATTR: RICH_TEXT_ATTRS,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'svg', 'img', 'form', 'input'],
    FORBID_ATTR: ['style', 'src', 'srcset'],
  });
}

function validationMax(field) {
  const rule = (field.validations ?? []).find((item) => item?.type === 'max');
  const configured = Number(field.validator?.maxLength ?? rule?.value ?? field.maxLength);
  if (Number.isFinite(configured) && configured > 0) return configured;
  if (field.type === 'email' || field.formatter === 'email') return 255;
  if (field.formatter === 'phone' || field.formatter === 'digits') return 32;
  if (field.formatter === 'name') return 100;
  if (field.type === 'text-editor') return 50000;
  if (field.type === 'textarea') return 5000;
  return 5000;
}

function isMultiline(field) {
  return field.type === 'textarea' || field.type === 'text-editor';
}

export function normalizeSecureString(value, field = {}) {
  if (typeof value !== 'string') return value;
  if (field.type === 'text-editor') return sanitizeRichText(value.normalize('NFC'));

  let normalized = value.normalize('NFC')
    .replace(INVISIBLE_RE, '')
    .replace(/\u00A0/g, ' ')
    .replace(CONTROL_RE, '');
  if (isMultiline(field)) {
    normalized = normalized.replace(/\r\n?/g, '\n').replace(/[ \t]{2,}/g, ' ').trim();
  } else {
    normalized = normalized.replace(/\s+/g, ' ').trim();
  }
  return normalized;
}

export function validateSecureString(value, field = {}) {
  if (typeof value !== 'string' || value === '') return null;
  const label = field.label || field.field || 'Field';
  const canonical = canonicalizeForSecurityScan(value);

  if (canonical.includes('\0') || canonical.includes('\u0000')) return `${label} contains a null byte`;
  if (field.type !== 'text-editor') {
    const attack = ATTACK_PATTERNS.find(({ re }) => re.test(canonical));
    if (attack) return `${label}: ${attack.message}`;
  } else {
    const dangerousRichText = ATTACK_PATTERNS.slice(0, 3).find(({ re }) => re.test(canonical));
    if (dangerousRichText) return `${label}: ${dangerousRichText.message}`;
  }

  const normalized = normalizeSecureString(value, field);
  if ([...normalized].length > validationMax(field)) return `${label} is too long`;
  if (field.formatter === 'name' && normalized && !NAME_RE.test(normalized)) {
    return `${label} allows only letters, numbers, spaces, apostrophes, hyphens and periods`;
  }
  if ((field.formatter === 'phone' || field.formatter === 'digits') && normalized && !PHONE_RE.test(normalized)) {
    return `${label} contains invalid phone characters`;
  }
  if (field.type === 'number' && normalized && !NUMBER_RE.test(normalized)) {
    return `${label} must contain only a valid number`;
  }
  if ((field.type === 'url' || field.formatter === 'url') && normalized && !/^https?:\/\//i.test(normalized)) {
    return `${label} must use http:// or https://`;
  }
  return null;
}

export function securityValidationRule(field) {
  return {
    validator: (_, value) => {
      const values = Array.isArray(value) ? value : [value];
      const error = values.map((item) => validateSecureString(item, field)).find(Boolean);
      return error ? Promise.reject(new Error(error)) : Promise.resolve();
    },
  };
}

function policyMap(groups = []) {
  const policies = new Map();
  groups.forEach((group) => {
    (group.fields ?? []).forEach((field) => {
      if (field.type === 'file') return;
      const destination = field.payloadKey || field.field;
      if (!destination) return;
      const path = group.addRow ? `${group.payloadKey || group.name}[].${destination}` : destination;
      policies.set(path, field);
    });
  });
  return policies;
}

export function securePayload(payload, groups = []) {
  const policies = policyMap(groups);
  const walk = (node, path = '') => {
    if (typeof node === 'string') {
      const field = policies.get(path) ?? {};
      const error = validateSecureString(node, field);
      if (error) throw new Error(error);
      return normalizeSecureString(node, field);
    }
    if (Array.isArray(node)) return node.map((item) => walk(item, `${path}[]`));
    if (node && typeof node === 'object') {
      return Object.fromEntries(Object.entries(node).map(([key, value]) => {
        if (key.startsWith('$') || key.includes('.') || key.includes('\0')) {
          throw new Error(`Unsafe object key: ${key}`);
        }
        const childPath = path ? `${path}.${key}` : key;
        return [key, walk(value, childPath)];
      }));
    }
    return node;
  };
  return walk(payload);
}
